import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { Modal, FileCCE } from "../components/ui";
import { Folder, FolderOpen, FileText, Download, ShieldCheck } from "lucide-react";

/**
 * Repositorio — agrupa snapshots por año, módulo y subsección.
 * Cada 6 meses (o manualmente) genera un snapshot que se almacena en repository_snapshots.
 * Estructura visual: SGA_XEROME_<año> → módulo → archivos.
 */
export default function Repository() {
  const [snapshots, setSnapshots] = useState([]);
  const [yearOpen, setYearOpen] = useState({});
  const [moduleOpen, setModuleOpen] = useState({});
  const [creating, setCreating] = useState(false);
  const [previewModule, setPreviewModule] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/data/repository_snapshots");
      setSnapshots(data || []);
    } catch (e) { console.error(e); }
  }
  useEffect(() => { load(); }, []);

  async function createSnapshot() {
    const period = window.prompt("Etiqueta del snapshot (ej: '2026 H1' o '2026 H2'):", `${new Date().getFullYear()} H${new Date().getMonth() < 6 ? 1 : 2}`);
    if (!period) return;
    setCreating(true);
    try {
      // Generate the list of modules with currently-stored documents
      const modules = [
        { key: "wells", label: "Pozos CONAGUA" },
        { key: "ptar", label: "PTAR" },
        { key: "fuel_areas", label: "Combustibles por Patio" },
        { key: "legal_requirements", label: "Requisitos Legales" },
        { key: "procedures", label: "Procedimientos ISO" },
        { key: "iso_objectives", label: "Objetivos ISO" },
        { key: "racs", label: "RACs Globales" },
        { key: "field_walks", label: "Hallazgos de Recorridos" },
        { key: "toa_cards", label: "TOA" },
        { key: "esg_programs", label: "Programas Ambientales" },
        { key: "esg_campaigns", label: "Campañas ESG" },
        { key: "esg_sustainability_reports", label: "Reportes de Sostenibilidad" },
        { key: "esg_materiality", label: "Estudios de Materialidad" },
        { key: "brigades", label: "Brigadistas" },
        { key: "msds", label: "HDS" },
        { key: "emergency_reports", label: "Reportes de Emergencia" },
        { key: "emergency_procedures", label: "Procedimientos de Atención" },
        { key: "pemex_volumes", label: "Validación PEMEX" },
        { key: "pemex_species_rescue", label: "Rescate de Especies PEMEX" },
        { key: "formats", label: "Formatos" },
      ];
      const year = new Date().getFullYear();
      await api.post("/data/repository_snapshots", {
        year,
        period,
        label: `SGA_XEROME_${year}`,
        modules: modules.map((m) => m.key),
        modules_labels: modules.map((m) => m.label),
        created_at_label: new Date().toLocaleString("es-MX"),
      });
      load();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    } finally { setCreating(false); }
  }

  // Group snapshots by year
  const byYear = {};
  snapshots.forEach((s) => {
    const y = s.year || new Date(s.created_at).getFullYear();
    if (!byYear[y]) byYear[y] = [];
    byYear[y].push(s);
  });
  const years = Object.keys(byYear).sort((a, b) => Number(b) - Number(a));

  return (
    <div data-testid="repository-module">
      <div className="flex items-center justify-between mb-3">
        <h1 className="card-h1" style={{ margin: 0 }}><ShieldCheck className="inline-block mr-2" size={22} /> Repositorio de Documentación</h1>
        <button className="btn btn-primary btn-sm" onClick={createSnapshot} disabled={creating} data-testid="repo-create-snapshot">
          {creating ? <span className="spinner" /> : "+ Generar snapshot semestral"}
        </button>
      </div>
      <p className="text-xs text-dim mb-3">
        Cada 6 meses (o manualmente con el botón superior) se genera una copia de seguridad de toda la documentación cargada por módulo,
        agrupada en carpetas por año al estilo SGA_XEROME_AAAA. Click en una carpeta de módulo para previsualizar el contenido.
      </p>

      {years.length === 0 && (
        <div className="card text-center text-dim">
          <Folder size={48} className="inline-block mb-3" />
          <div>Aún no se han generado snapshots. Usa el botón <b>+ Generar snapshot semestral</b> para crear el primero.</div>
        </div>
      )}

      {years.map((y) => (
        <div key={y} className="card mb-3" data-testid={`repo-year-${y}`}>
          <div
            className="flex items-center gap-2 mb-2 cursor-pointer"
            onClick={() => setYearOpen((s) => ({ ...s, [y]: !s[y] }))}
            style={{ cursor: "pointer", padding: 4 }}
          >
            {yearOpen[y] ? <FolderOpen size={20} color="#f1c40f" /> : <Folder size={20} color="#f1c40f" />}
            <b style={{ fontSize: 16 }}>SGA_XEROME_{y}</b>
            <span className="text-xs text-dim">({byYear[y].length} snapshot{byYear[y].length !== 1 ? "s" : ""})</span>
          </div>
          {yearOpen[y] && byYear[y].map((snap) => (
            <div key={snap.id} className="ml-6 mb-2" style={{ borderLeft: "1px solid var(--border)", paddingLeft: 12 }}>
              <div
                className="flex items-center gap-2 cursor-pointer"
                onClick={() => setModuleOpen((s) => ({ ...s, [snap.id]: !s[snap.id] }))}
                style={{ cursor: "pointer", padding: 4 }}
              >
                {moduleOpen[snap.id] ? <FolderOpen size={16} color="#3498db" /> : <Folder size={16} color="#3498db" />}
                <span>{snap.period || snap.label || snap.id}</span>
                <span className="text-xs text-dim">— {snap.created_at_label || snap.created_at}</span>
              </div>
              {moduleOpen[snap.id] && (
                <div className="ml-6 mt-2 grid grid-cols-3 gap-2">
                  {(snap.modules || []).map((mk, i) => {
                    const lbl = (snap.modules_labels || [])[i] || mk;
                    return (
                      <div
                        key={mk}
                        className="card cursor-pointer"
                        style={{ padding: 8, cursor: "pointer" }}
                        onClick={() => setPreviewModule({ key: mk, label: lbl, snapshotId: snap.id })}
                        data-testid={`repo-mod-${mk}`}
                      >
                        <Folder size={14} className="inline-block mr-1" />
                        <span style={{ fontSize: 13 }}>{lbl}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      <Modal
        open={!!previewModule}
        onClose={() => setPreviewModule(null)}
        title={`Repositorio — ${previewModule?.label || ""}`}
        size="xl"
      >
        {previewModule && (
          <div>
            <p className="text-xs text-dim mb-2">Documentos asociados al módulo "{previewModule.label}".</p>
            <FileCCE module={previewModule.key} />
          </div>
        )}
      </Modal>
    </div>
  );
}
